/**
 * Document Verification Result Component
 * Displays the result of a QR code signature verification.
 * Shows document data if valid, error message if invalid.
 * Includes a download button when online.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  Divider,
  CircularProgress,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  GppBad as InvalidIcon,
  Download as DownloadIcon,
  Description as DocIcon,
} from '@mui/icons-material';

import type { VerificationResult, DocumentData, LvoDocumentData, TaeDocumentData, InvoiceDocumentData } from '../../utils/documentVerification';
import { getApiBaseUrl } from '../../config/env';
import { formatDate } from '../../utils/dateFormat';

/** Signed dates are "YYYY-MM-DD"; anything else is shown as signed. */
const signedDate = (value: string) => formatDate(value, undefined, value);

interface DocumentVerificationResultProps {
  result: VerificationResult;
  onClose?: () => void;
}

/** Format a label for display */
function DocField({ label, value }: { label: string; value: string }) {
  const muiTheme = useMuiTheme();
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: muiTheme.palette.text.primary, textAlign: 'end', ml: 2 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function DocumentVerificationResult({
  result,
}: DocumentVerificationResultProps) {
  const { t } = useTranslation();
  const muiTheme = useMuiTheme();
  const [downloading, setDownloading] = useState(false);
  const [isOnline] = useState(() => navigator.onLine);

  const handleDownload = async () => {
    if (!result.reference) return;
    setDownloading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/documents/verify/${result.reference}/pdf`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${result.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Silent fail - user can see the data anyway
    } finally {
      setDownloading(false);
    }
  };

  // Invalid signature
  if (!result.valid) {
    // Unrecognized QR code (not from BNFT at all)
    if (result.error === 'UNRECOGNIZED_QR') {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <InvalidIcon sx={{ fontSize: 64, color: 'error.main', mb: 1 }} />
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('common.documentVerification.unrecognizedQR')}
          </Alert>
        </Box>
      );
    }

    // Legacy QR codes (old URL format) - show specific message
    if (result.error === 'LEGACY_FORMAT') {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <InvalidIcon sx={{ fontSize: 64, color: 'warning.main', mb: 1 }} />
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('common.documentVerification.legacyFormat')}
          </Alert>
        </Box>
      );
    }

    const errorKey = result.error === 'PUBLIC_KEY_NOT_AVAILABLE'
      ? 'common.documentVerification.publicKeyNotAvailable'
      : result.error === 'INVALID_FORMAT'
        ? 'common.documentVerification.invalidFormat'
        : 'common.documentVerification.invalidSignature';

    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <InvalidIcon sx={{ fontSize: 64, color: 'error.main', mb: 1 }} />
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(errorKey)}
        </Alert>
      </Box>
    );
  }

  // Valid signature - render document data
  const data = result.data!;
  const docTypeLabel =
    data.t === 'LVO' ? t('common.documentVerification.types.lvo') :
    data.t === 'TAE' ? t('common.documentVerification.types.tae') :
    t('common.documentVerification.types.invoice');

  const docTypeColor =
    data.t === 'LVO' ? 'primary' :
    data.t === 'TAE' ? 'warning' :
    'info';

  return (
    <Box sx={{ py: 1 }}>
      {/* Validity badge */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <VerifiedIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
          {t('common.documentVerification.validDocument')}
        </Typography>
      </Box>

      {/* Document type chip */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Chip
          icon={<DocIcon />}
          label={docTypeLabel}
          color={docTypeColor as any}
          variant="filled"
          size="medium"
          sx={{ fontWeight: 600, fontSize: '0.95rem', px: 1 }}
        />
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Document fields */}
      <Box sx={{
        backgroundColor: muiTheme.palette.action.hover,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}>
        {'r' in data && (
          <DocField
            label={t('common.documentVerification.fields.reference')}
            value={data.r}
          />
        )}
        {'d' in data && (
          <DocField
            label={t('common.documentVerification.fields.date')}
            value={signedDate(data.d)}
          />
        )}

        {/* LVO specific fields — each one rendered only if the key is in the QR JSON */}
        {data.t === 'LVO' && (() => {
          const lvo = data as LvoDocumentData;
          return (
            <>
              {'s' in lvo && <DocField label={t('common.documentVerification.fields.structure')} value={lvo.s} />}
              {'dep' in lvo && <DocField label={t('common.documentVerification.fields.departure')} value={`${lvo.dep}${'depC' in lvo ? ` (${lvo.depC})` : ''}`} />}
              {'arr' in lvo && <DocField label={t('common.documentVerification.fields.arrival')} value={`${lvo.arr}${'arrC' in lvo ? ` (${lvo.arrC})` : ''}`} />}
              {'exp' in lvo && <DocField label={t('common.documentVerification.fields.sender')} value={lvo.exp} />}
              {'dest' in lvo && <DocField label={t('common.documentVerification.fields.recipient')} value={lvo.dest} />}
              {'tr' in lvo && <DocField label={t('common.documentVerification.fields.transporter')} value={lvo.tr} />}
              {'v' in lvo && <DocField label={t('common.documentVerification.fields.vehicle')} value={lvo.v} />}
              {'v2' in lvo && <DocField label={t('common.documentVerification.fields.trailer')} value={lvo.v2} />}
              {'dr' in lvo && <DocField label={t('common.documentVerification.fields.driver')} value={lvo.dr} />}
              {'drL' in lvo && <DocField label={t('common.documentVerification.fields.driverLicense')} value={lvo.drL} />}
              {'gds' in lvo && <DocField label={t('common.documentVerification.fields.goods')} value={lvo.gds} />}
              {'itn' in lvo && <DocField label={t('common.documentVerification.fields.itinerary')} value={lvo.itn} />}
              {'px' in lvo && <DocField label={t('common.documentVerification.fields.transportPrice')} value={`${Number(lvo.px).toLocaleString()} XAF`} />}
              {'fee' in lvo && <DocField label={t('common.documentVerification.fields.fee')} value={`${Number(lvo.fee).toLocaleString()} XAF`} />}
              {'q' in lvo && lvo.q === '1' && (
                <DocField label={t('common.documentVerification.fields.quotaType')} value="QUOTA" />
              )}
            </>
          );
        })()}

        {/* TAE specific fields — each one rendered only if the key is in the QR JSON */}
        {data.t === 'TAE' && (() => {
          const tae = data as TaeDocumentData;
          return (
            <>
              {'own' in tae && <DocField label={t('common.documentVerification.fields.owner')} value={tae.own} />}
              {'veh' in tae && <DocField label={t('common.documentVerification.fields.vehicle')} value={tae.veh} />}
              {'vin' in tae && <DocField label={t('common.documentVerification.fields.chassis')} value={tae.vin} />}
              {'vC' in tae && <DocField label={t('common.documentVerification.fields.vehicleCountry')} value={tae.vC} />}
              {'ag' in tae && <DocField label={t('common.documentVerification.fields.agency')} value={tae.ag} />}
              {'vf' in tae && <DocField label={t('common.documentVerification.fields.validFrom')} value={signedDate(tae.vf)} />}
              {'vu' in tae && <DocField label={t('common.documentVerification.fields.validUntil')} value={signedDate(tae.vu)} />}
              {'mt' in tae && <DocField label={t('common.documentVerification.fields.amount')} value={`${Number(tae.mt).toLocaleString()} XAF`} />}
              {'val' in tae && <DocField label={t('common.documentVerification.fields.validity')} value={`${tae.val} ${t('common.documentVerification.fields.days')}`} />}
            </>
          );
        })()}

        {/* Invoice specific fields — each one rendered only if the key is in the QR JSON */}
        {data.t === 'INV' && (() => {
          const inv = data as InvoiceDocumentData;
          return (
            <>
              {'ti' in inv && <DocField label={t('common.documentVerification.fields.invoiceTitle')} value={inv.ti} />}
              {'cl' in inv && <DocField label={t('common.documentVerification.fields.client')} value={inv.cl} />}
              {'bn' in inv && <DocField label={t('common.documentVerification.fields.beneficiary')} value={inv.bn} />}
              {'mt' in inv && <DocField label={t('common.documentVerification.fields.amount')} value={`${Number(inv.mt).toLocaleString()} ${'cur' in inv ? inv.cur : 'XAF'}`} />}
              {'pd' in inv && <DocField label={t('common.documentVerification.fields.paidAmount')} value={`${Number(inv.pd).toLocaleString()} ${'cur' in inv ? inv.cur : 'XAF'}`} />}
              {'st' in inv && <DocField label={t('common.documentVerification.fields.status')} value={inv.st === 'PAID' ? t('common.documentVerification.fields.paid') : t('common.documentVerification.fields.unpaid')} />}
              {'dpay' in inv && <DocField label={t('common.documentVerification.fields.paymentDate')} value={signedDate(inv.dpay)} />}
            </>
          );
        })()}
      </Box>

      {/* Download button (online only, LVO and TAE only - invoices use a different endpoint) */}
      {isOnline && result.reference && data.t !== 'INV' && (
        <Button
          variant="contained"
          fullWidth
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {downloading
            ? t('common.documentVerification.downloading')
            : t('common.documentVerification.downloadPdf')}
        </Button>
      )}

      {!isOnline && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('common.documentVerification.offlineMessage')}
        </Alert>
      )}
    </Box>
  );
}
